import axios from 'axios';
import { useEffect, useState } from 'react';

import * as voodooSdk from '@gmm/sdk-voodoo';
import { LockedProblemResponse } from '@gmm/sdk-voodoo/examProblems';
import { ProblemFromSWR } from '~/lib/problemPreview/problemPreview';

import { useExerciseLastUpdatedStore } from '../../stores/exerciseLastUpdatedStore';
import { useSkillDiscoveryLockedSkillsStore } from '../../stores/skillDiscoveryLockedSkillStore';
import { useDuplicationWarning } from '../useReloadSkillDiscovery';

export const useLockedSkillDiscoveryProblem = (
  examId: string,
  skillId: string,
  positionWithinSkill: number,
): ProblemFromSWR => {
  const [addLockedProblem, getLockedProblem] =
    useSkillDiscoveryLockedSkillsStore(state => [
      state.addLockedProblem,
      state.getLockedProblem,
    ]);
  const getLastUpdated = useExerciseLastUpdatedStore(
    state => state.getLastUpdated,
  );
  const { showDuplicationWarning } = useDuplicationWarning();

  const lockedProblem = getLockedProblem(examId, skillId, positionWithinSkill);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (lockedProblem || loading) return;

    const fetchLockedProblem = async (): Promise<void> => {
      setLoading(true);

      try {
        const config = voodooSdk.examProblems.show(
          examId,
          skillId,
          positionWithinSkill,
          false, // not refresh
          true, // SkillDiscovery
          getLastUpdated(examId),
        );
        const { data } = await axios.request<LockedProblemResponse>(config);

        const newLockedProblem = {
          positionWithinSkill,
          problemAndAnswer: data.clientProblem,
        };

        addLockedProblem(examId, skillId, newLockedProblem);
        setError(null);
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          showDuplicationWarning('UNAVAILABLE_RELOADING');
        } else {
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLockedProblem();
  }, [examId, skillId, positionWithinSkill, lockedProblem]);

  return {
    problemData: lockedProblem?.problemAndAnswer,
    isValidating: loading,
    error,
  };
};
